import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Sparkles, X, Send, Mic, MicOff, Loader2, RotateCcw, Bot } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWhisperRecorder } from "@/lib/useWhisperRecorder";

const QUICK_QUESTIONS = [
  "Was ist eine Monoblock-Wärmepumpe?",
  "Unterschied Monoblock vs. Split?",
  "Wie wird die Heizlast berechnet?",
  "Was sind Vollbenutzungsstunden?",
  "Welche Vorlauftemperatur ist optimal für eine WP?",
  "Wie funktioniert ein hydraulischer Abgleich?",
  "BAFA / KfW Förderung für Wärmepumpen?",
  "Was ist der SCOP-Wert?",
  "Schallschutz: Welche Abstände gelten?",
  "Wann ist ein Pufferspeicher nötig?",
];

const STORAGE_KEY = "novatus_ai_chat";

const renderMarkdown = (text) => {
  if (!text) return null;
  return text.split("\n\n").map((para, pIdx) => {
    if (para.startsWith("# ")) {
      return <h3 key={pIdx} className="font-display font-bold text-[16px] text-[#00E575] mt-1 mb-1">{para.slice(2)}</h3>;
    }
    if (para.startsWith("## ")) {
      return <h4 key={pIdx} className="font-display font-bold text-[14px] text-zinc-100 mt-1 mb-1">{para.slice(3)}</h4>;
    }
    const lines = para.split("\n");
    if (lines.every((l) => l.trim().startsWith("- ") || l.trim().startsWith("* "))) {
      return (
        <ul key={pIdx} className="my-1 space-y-1 list-none pl-0">
          {lines.map((l, i) => (
            <li key={i} className="flex gap-2"><span className="text-[#00E575] mt-1">•</span><span>{renderInline(l.replace(/^[-*]\s/, ""))}</span></li>
          ))}
        </ul>
      );
    }
    return <p key={pIdx} className="my-1.5 leading-relaxed">{renderInline(para)}</p>;
  });
};

const renderInline = (text) => {
  // Bold **x**
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="text-white font-bold">{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
};

export const AiAssistant = () => {
  const { user } = useAuth();
  const params = useParams();
  const location = useLocation();
  const projectId = params?.id || null;

  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Voice input via Whisper-1 (works on iOS Safari, Android, Desktop)
  const recorder = useWhisperRecorder({
    language: "de",
    onTranscript: (text) => setInput((prev) => (prev ? prev + " " : "") + text),
    onError: (msg) => toast.error(msg),
  });

  // Load history from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setHistory(JSON.parse(raw));
    } catch (_e) { /* noop */ }
  }, []);

  // Persist history
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(-30)));
    } catch (_e) { /* noop */ }
  }, [history]);

  // Auto scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, loading, open]);

  // External "open AI" event – the Help system fires this with an optional
  // seed question. We open the panel and pre-fill the input.
  useEffect(() => {
    const handler = (e) => {
      setOpen(true);
      const seed = e?.detail?.seed;
      if (seed) setInput(seed);
    };
    window.addEventListener("novatus:ai-open", handler);
    return () => window.removeEventListener("novatus:ai-open", handler);
  }, []);

  const micSupported = recorder.supported;

  const onMicClick = () => {
    if (recorder.transcribing) return;
    if (recorder.recording) recorder.stop();
    else recorder.start();
  };

  const ask = async (qOverride) => {
    const q = (qOverride || input).trim();
    if (!q || loading) return;
    const newHistory = [...history, { role: "user", content: q }];
    setHistory(newHistory);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/ai/ask", {
        question: q,
        history: history.slice(-6),
        project_id: projectId,
      });
      const fullText = r.data.answer || "";
      // Loader-Bubble ausblenden, leeres Assistant-Bubble einsetzen
      const baseHistory = [...newHistory, { role: "assistant", content: "" }];
      setHistory(baseHistory);
      setLoading(false);

      // Typewriter-Streaming: setzt den Text Stück für Stück. Bei der lokalen
      // Knowledge-Base (kb-local) langsamer animieren, bei LLM-Antworten zügiger
      // damit die Latenz nicht künstlich erhöht wird.
      const isLocal = r.data.model === "kb-local";
      const charsPerTick = isLocal ? 2 : 4;
      const tickMs = isLocal ? 18 : 12;
      let i = 0;
      await new Promise((resolve) => {
        const id = setInterval(() => {
          i += charsPerTick;
          const partial = fullText.slice(0, i);
          setHistory((h) => {
            const copy = [...h];
            copy[copy.length - 1] = { role: "assistant", content: partial };
            return copy;
          });
          if (i >= fullText.length) {
            clearInterval(id);
            resolve();
          }
        }, tickMs);
      });
    } catch (e) {
      const msg = e.response?.data?.detail || "KI-Antwort fehlgeschlagen";
      setHistory([...newHistory, { role: "assistant", content: `⚠️ ${msg}` }]);
      if (e.response?.status === 402) {
        toast.error("KI-Budget erschöpft — bitte Universal Key auffüllen");
      } else {
        toast.error(msg);
      }
      setLoading(false);
    }
  };

  const reset = () => {
    setHistory([]);
    localStorage.removeItem(STORAGE_KEY);
    toast.success("Verlauf gelöscht");
  };

  if (!user) return null;
  // Hide on full-canvas drawing routes – overlaps the toolbar.
  if (location.pathname.endsWith("/sketch") || location.pathname.endsWith("/annotate")) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button */}
      {!open && (
        <button
          data-testid="ai-assistant-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-4 left-4 sm:left-6 z-40 h-14 w-14 rounded-2xl bg-[#00E575] text-[#09090B] shadow-[0_10px_25px_rgba(0,229,117,0.45)] flex items-center justify-center active:scale-95 transition hover:bg-[#00C964]"
          aria-label="KI-Assistent öffnen"
          title="KI-Assistent fragen"
        >
          <Sparkles size={26} strokeWidth={2.2} />
        </button>
      )}

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Slide-up Panel */}
      <div
        data-testid="ai-assistant-panel"
        className={`fixed left-0 right-0 bottom-0 z-50 bg-[#0E0E12] border-t border-[#00E575]/40 rounded-t-3xl shadow-2xl transition-transform duration-300 ${
          open ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ height: "85vh", maxHeight: "780px" }}
      >
        <div className="h-full max-w-3xl mx-auto flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#00E575]/20 flex items-center justify-center">
                <Bot className="text-[#00E575]" size={20} />
              </div>
              <div>
                <div className="font-display font-bold text-[16px] text-white">KI-Assistent</div>
                <div className="text-[12px] text-zinc-400">Wärmepumpen-Berater · Live im Kundengespräch</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {history.length > 0 && (
                <button
                  data-testid="ai-assistant-reset"
                  onClick={reset}
                  className="h-9 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-300 text-[12px] inline-flex items-center gap-1.5 font-medium"
                >
                  <RotateCcw size={13} /> Neu
                </button>
              )}
              <button
                data-testid="ai-assistant-close"
                onClick={() => setOpen(false)}
                className="h-10 w-10 rounded-xl bg-zinc-800/60 hover:bg-zinc-800 flex items-center justify-center"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Chat list */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {history.length === 0 && (
              <div className="text-center mt-2">
                <div className="inline-flex h-14 w-14 rounded-2xl bg-[#00E575]/15 items-center justify-center mb-3">
                  <Sparkles className="text-[#00E575]" size={28} />
                </div>
                <h3 className="font-display font-bold text-[18px] text-white mb-1">Was möchten Sie wissen?</h3>
                <p className="text-[13px] text-zinc-400 mb-5 px-2">Fachfragen zu Wärmepumpen, Heizlast, Förderung & Co. — direkt im Kundengespräch.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
                  {QUICK_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      data-testid={`ai-quick-${q.slice(0,20)}`}
                      onClick={() => ask(q)}
                      disabled={loading}
                      className="text-left text-[13px] px-3 py-2.5 rounded-xl bg-[#1A1A20] border border-zinc-800 hover:border-[#00E575]/50 hover:bg-[#00E575]/5 text-zinc-200 transition active:scale-[0.99]"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {history.map((m, idx) => (
              <div key={idx} data-testid={`ai-msg-${idx}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[88%] rounded-2xl px-4 py-3 ${
                  m.role === "user"
                    ? "bg-[#00E575] text-[#09090B] font-medium"
                    : "bg-[#1A1A20] border border-zinc-800 text-zinc-100"
                }`}>
                  {m.role === "user" ? (
                    <div className="text-[14px] leading-relaxed whitespace-pre-wrap">{m.content}</div>
                  ) : (
                    <div className="text-[14px]">{renderMarkdown(m.content)}</div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#1A1A20] border border-zinc-800 rounded-2xl px-4 py-3 inline-flex items-center gap-2 text-zinc-400 text-[13px]">
                  <Loader2 size={14} className="animate-spin text-[#00E575]" />
                  KI denkt nach…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-zinc-800 p-3">
            <div className="flex gap-2 items-end">
              <div className="flex-1 relative">
                <textarea
                  data-testid="ai-assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      ask();
                    }
                  }}
                  placeholder="Frage stellen… (Shift+Enter für Zeilenumbruch)"
                  rows={1}
                  className="w-full rounded-xl px-4 py-3 pr-12 text-[14px] resize-none focus:ring-1 transition outline-none border max-h-32"
                />
                {micSupported && (
                  <button
                    data-testid="ai-assistant-mic"
                    onClick={onMicClick}
                    disabled={recorder.transcribing}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-lg flex items-center justify-center transition disabled:opacity-60 ${
                      recorder.recording
                        ? "bg-red-500 text-white shadow-[0_0_12px_rgba(239,68,68,0.5)] animate-pulse"
                        : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                    }`}
                    title={
                      recorder.transcribing
                        ? "Transkribiere…"
                        : recorder.recording
                        ? "Aufnahme beenden"
                        : "Diktieren"
                    }
                  >
                    {recorder.transcribing ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : recorder.recording ? (
                      <MicOff size={16} />
                    ) : (
                      <Mic size={16} />
                    )}
                  </button>
                )}
              </div>
              <button
                data-testid="ai-assistant-send"
                onClick={() => ask()}
                disabled={loading || !input.trim()}
                className="h-12 px-4 rounded-xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center gap-1.5 hover:bg-[#00C964] glow-green disabled:opacity-40 disabled:hover:bg-[#00E575] transition active:scale-95"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </div>
            <div className="text-[12px] text-zinc-300 mt-1.5 px-1 text-center">
              KI-Antworten dienen als Hilfestellung – fachliche Prüfung im Einzelfall erforderlich.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
